import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { SetuMark } from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!token) { toast.error("Missing token"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
            <span className="font-display text-2xl tracking-tight">Setubuild.OS</span>
          </Link>
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
        </div>
      </header>
      <main className="flex-1 grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="reset-form">
          <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-primary">
            <ShieldCheck className="h-3 w-3" /> Secure reset
          </div>
          <h1 className="font-display text-4xl">Set a new password</h1>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">New password</label>
            <input required type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              data-testid="reset-pw"
              className="w-full mt-1 border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
            <div className="mono text-[10px] text-muted-foreground mt-1">Min 8 characters.</div>
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Confirm</label>
            <input required type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-pw2"
              className="w-full mt-1 border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-primary" />
          </div>
          <button type="submit" disabled={busy || !token} data-testid="reset-submit"
            className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 disabled:opacity-50">
            {busy ? "…" : "Reset password"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </main>
    </div>
  );
}
